import { apiClient } from './client';

// ─── Types ──────────────────────────────────────────────────────────

export type StudyForm = 'kunduzgi' | 'sirtqi' | 'kechki' | 'masofaviy';
export type StudyLanguage = 'uz' | 'ru' | 'kk' | 'qq' | 'ky' | 'tk' | 'tg' | 'en';

export interface ParsedSubject {
  name: string;
  coefficient: number;
  subject_slug: string | null;
}

export interface University {
  id: string;
  name: string;
  name_ru: string | null;
  name_kiril: string | null;
  slug: string;
  location: string | null;
  phone: string | null;
  website: string | null;
  logo_url: string | null;
  region: string | null;
  is_active: boolean;
  directions_count?: number;
  created_at: string;
  updated_at: string;
}

export interface SubjectRef {
  id: string;
  slug: string;
  name: string;
  name_ru: string | null;
}

export interface DirectionYearlyQuota {
  id: string;
  direction_id: string;
  year: number;
  form: StudyForm;
  language: StudyLanguage;
  subjects: ParsedSubject[];
  subjects_raw: string | null;
  primary_subject_id: string | null;
  primary_subject?: SubjectRef | null;
  primary_coefficient: number;
  secondary_subject_id: string | null;
  secondary_subject?: SubjectRef | null;
  secondary_coefficient: number;
  grant_score: number | null;
  contract_score: number | null;
  grant_count: number | null;
  contract_count: number | null;
}

export interface UniversityDirection {
  id: string;
  university_id: string;
  code: string;
  name_uz: string;
  name_ru: string | null;
  is_active: boolean;
  quotas: DirectionYearlyQuota[];
  created_at: string;
  updated_at: string;
}

export interface UniversityWithDirections extends University {
  directions: UniversityDirection[];
}

export interface CreateUniversityDto {
  name: string;
  name_ru?: string;
  name_kiril?: string;
  slug: string;
  location?: string;
  phone?: string;
  website?: string;
  logo_url?: string;
  region?: string;
}

export interface UpdateUniversityDto extends Partial<CreateUniversityDto> {
  is_active?: boolean;
}

export interface CreateDirectionDto {
  university_id: string;
  code: string;
  name_uz: string;
  name_ru?: string;
}

export interface UpdateDirectionDto extends Partial<Omit<CreateDirectionDto, 'university_id'>> {
  is_active?: boolean;
}

export interface CreateQuotaDto {
  direction_id: string;
  year: number;
  form: StudyForm;
  language?: StudyLanguage;
  subjects?: ParsedSubject[];
  subjects_raw?: string;
  primary_subject_id?: string;
  primary_coefficient?: number;
  secondary_subject_id?: string;
  secondary_coefficient?: number;
  grant_score?: number;
  contract_score?: number;
  grant_count?: number;
  contract_count?: number;
}

export interface UpdateQuotaDto extends Partial<Omit<CreateQuotaDto, 'direction_id' | 'year' | 'form'>> {}

export interface UniversitiesListResponse {
  data: University[];
  total: number;
  page: number;
  limit: number;
  year: number | null;
}

export interface DirectionsResponse {
  directions: UniversityDirection[];
  stats: {
    total: number;
    total_quotas: number;
    highest_grant: number;
    lowest_contract: number;
  };
  year: number | null;
}

export interface GetUniversitiesParams {
  page?: number;
  limit?: number;
  search?: string;
  region?: string;
  year?: number;
}

export interface GetDirectionsParams {
  search?: string;
  sort_by?: 'name_uz' | 'code' | 'grant_score' | 'contract_score';
  sort_order?: 'ASC' | 'DESC';
  year?: number;
  form?: StudyForm;
  language?: StudyLanguage;
}

// ─── API ────────────────────────────────────────────────────────────

export const universitiesApi = {
  getAll: async (params: GetUniversitiesParams = {}): Promise<UniversitiesListResponse> => {
    const response = await apiClient.get<UniversitiesListResponse>('/universities', { params });
    return response.data;
  },

  getById: async (id: string): Promise<UniversityWithDirections> => {
    const response = await apiClient.get<UniversityWithDirections>(`/universities/${id}`);
    return response.data;
  },

  getDirections: async (
    id: string,
    params?: GetDirectionsParams,
  ): Promise<DirectionsResponse> => {
    const response = await apiClient.get<DirectionsResponse>(
      `/universities/${id}/directions`,
      { params },
    );
    return response.data;
  },

  getYears: async (): Promise<number[]> => {
    const response = await apiClient.get<number[]>('/universities/years');
    return response.data;
  },

  getForms: async (): Promise<StudyForm[]> => {
    const response = await apiClient.get<StudyForm[]>('/universities/forms');
    return response.data;
  },

  getRegions: async (): Promise<string[]> => {
    const response = await apiClient.get<string[]>('/universities/regions');
    return response.data;
  },

  create: async (dto: CreateUniversityDto): Promise<University> => {
    const response = await apiClient.post<University>('/admin/universities', dto);
    return response.data;
  },

  update: async (id: string, dto: UpdateUniversityDto): Promise<University> => {
    const response = await apiClient.patch<University>(`/admin/universities/${id}`, dto);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/admin/universities/${id}`);
  },

  // Directions (registry) CRUD
  createDirection: async (dto: CreateDirectionDto): Promise<UniversityDirection> => {
    const response = await apiClient.post<UniversityDirection>(
      '/admin/universities/directions',
      dto,
    );
    return response.data;
  },

  updateDirection: async (id: string, dto: UpdateDirectionDto): Promise<UniversityDirection> => {
    const response = await apiClient.patch<UniversityDirection>(
      `/admin/universities/directions/${id}`,
      dto,
    );
    return response.data;
  },

  deleteDirection: async (id: string): Promise<void> => {
    await apiClient.delete(`/admin/universities/directions/${id}`);
  },

  // Yearly quotas CRUD
  createQuota: async (dto: CreateQuotaDto): Promise<DirectionYearlyQuota> => {
    const response = await apiClient.post<DirectionYearlyQuota>(
      '/admin/universities/quotas',
      dto,
    );
    return response.data;
  },

  updateQuota: async (id: string, dto: UpdateQuotaDto): Promise<DirectionYearlyQuota> => {
    const response = await apiClient.patch<DirectionYearlyQuota>(
      `/admin/universities/quotas/${id}`,
      dto,
    );
    return response.data;
  },

  deleteQuota: async (id: string): Promise<void> => {
    await apiClient.delete(`/admin/universities/quotas/${id}`);
  },
};
